/**
 * Per-run logs drill-in — the general (non-api_request) log records for one run,
 * from `useLogs` (GET /v1/runs/{producer}/{session_id}/logs).
 *
 * READ-ONLY. A severity `<select>` sets the FLOOR filter (default INFO); changing
 * it re-keys `useLogs`, which refetches from the first page. Cursor paging is
 * surfaced as a `Load more` button (visible only while `hasNextPage`); pages are
 * flattened and rendered in order so each `Load more` appends to the list.
 *
 * Bodies/attrs are opaque — the service never interprets them, and neither does
 * this view. `body_truncated` / `attrs_truncated` mark values the receiver
 * clipped; the marker just flags that, it does not reconstruct anything.
 *
 * Rows are keyed by their index in the flattened loaded list, NOT by `seq`:
 * `seq` is the record's index within its ingest request, so two records from
 * different requests in one session both carry `seq: 0` and collided on one
 * React key — a real duplicate-key bug (spec §5.4 of the 2026-10-27 timeline
 * design), not a tidy-up.
 */

import { useState } from 'react';
import { useLogs } from '../lib/queries';

/** The severity ladder, ascending. The floor filter matches this level and
 *  everything above it. */
export const SEVERITIES = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'FATAL'] as const;

/** The CSS class dressing a severity span, e.g. `WARN` → `log-sev-warn`. Colors
 *  are mapped to Saga tokens in index.css (muted trace/debug, text info,
 *  banner-text warn, danger error/fatal). */
export function severityClass(s: string): string {
  return `log-sev-${s.toLowerCase()}`;
}

export default function LogsPanel({
  producer,
  sessionId,
}: {
  /** The run's address — never a claimed platform. */
  producer: string;
  sessionId: string;
}) {
  const [severity, setSeverity] = useState('INFO');
  const { data, hasNextPage, fetchNextPage, isLoading, isError, refetch } = useLogs(
    producer,
    sessionId,
    severity,
  );

  const lines = (data?.pages ?? []).flatMap((p) => p.logs);

  const control = (
    <div className="logs-controls">
      <label className="runs-control">
        <span className="runs-control-label">Severity ≥</span>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  return (
    <>
      <h3 className="section-title">Logs</h3>
      {control}

      {isLoading ? (
        <p className="note">Loading logs…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load logs{' '}
          <button type="button" className="btn" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      ) : lines.length === 0 ? (
        <p className="note">No logs for this run.</p>
      ) : (
        <>
          <ul className="log-lines">
            {/* The row key below is POSITIONAL, because the response strips
                `srcKey`/`srcLine` and `seq` restarts per ingest request, so no
                globally unique field survives. One accepted consequence: an
                attrs `<details>` the reader opened can come back open over a
                DIFFERENT record when a page is re-fetched and the filtered set
                has moved — and only on a CACHED floor, since with no
                `placeholderData` anywhere an uncached floor change unmounts this
                list outright. Harmless presentation state; the open flag is the
                browser's, on an uncontrolled element, so resetting it would mean
                making it controlled. */}
            {lines.map((line, i) => (
              <li key={i} className="log-line">
                <span className="log-ts mono">{line.ts_iso}</span>
                <span className={`log-sev ${severityClass(line.severity)}`}>{line.severity}</span>
                {line.event_name && <span className="log-event">{line.event_name}</span>}
                <code className="log-body">{String(line.body)}</code>
                {line.body_truncated && <span className="truncated-marker">…truncated</span>}
                {line.attrs && Object.keys(line.attrs).length > 0 && (
                  <details className="log-attrs">
                    <summary>attrs{line.attrs_truncated ? ' (truncated)' : ''}</summary>
                    <pre>
                      <code>{JSON.stringify(line.attrs, null, 2)}</code>
                    </pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
          {hasNextPage && (
            <button type="button" className="btn" onClick={() => fetchNextPage()}>
              Load more
            </button>
          )}
        </>
      )}
    </>
  );
}
